import { createContext, useContext, useMemo, useState } from 'react';
import noop from 'utils/noop';

type ListContextState<T> = {
  items: T[];
  filteredItems: T[];
  filterFn: (item: T, query: string) => boolean;
  query: string;
  setQuery: (query: string) => void;
  hasNoItems?: boolean;
  hasNoMatchingItems?: boolean;
  currentCount?: number;
  totalCount?: number;
};

const defaultValues: ListContextState<any> = {
  items: [],
  filteredItems: [],
  filterFn: () => true,
  query: '',
  setQuery: noop,
  hasNoItems: false,
  hasNoMatchingItems: false,
  currentCount: 0,
  totalCount: 0,
};

export const ListContext = createContext<ListContextState<any>>(defaultValues);
export const useListContext = <T,>() => useContext<ListContextState<T>>(ListContext);

type ListContextProviderProps<T> = {
  items: T[];
  filterFn: (item: T, query: string) => boolean;
};

export const ListContextProvider = <T,>({
  children,
  items = [] as T[],
  filterFn,
}: React.PropsWithChildren<ListContextProviderProps<T>>) => {
  const [query, setQuery] = useState('');

  const filteredItems = useMemo(
    () => items.filter((item) => filterFn(item, query)),
    [items, query, filterFn],
  );

  const currentCount = filteredItems.length;
  const totalCount = items.length;

  const isEmpty = filteredItems.length === 0;
  const hasNoItems = !query && isEmpty;
  const hasNoMatchingItems = !!query && isEmpty;

  return (
    <ListContext.Provider
      value={{
        items,
        filterFn,
        query,
        setQuery,
        hasNoItems,
        hasNoMatchingItems,
        currentCount,
        totalCount,
        filteredItems,
      }}
    >
      {children}
    </ListContext.Provider>
  );
};
